import { Flex } from 'antd';
import './HistoricDevicePage.scss';
import { useCallback, useEffect, useState } from 'react';
import DateFilterComponent from '../../../components/utils/DateFilterComponent/DateFilterComponent';
import useTelemetryStore from '../../../stores/telemetry.store';
import { useParams } from 'react-router-dom';
import HighlightComponent from '../../../components/device/HistoricComponents/HighlightComponent/HighlightComponent';
import { DataTelemetryKPI } from '../../../models/telemetry';
import SpeedChartComponent from '../../../components/device/HistoricComponents/SpeedChartComponent/SpeedChartComponent';
import { DataTelemetry } from '../../../gen/proto/api/gateway/v1/gateway_pb';
import SensorChartComponent from '../../../components/device/HistoricComponents/SensorChartComponent/SensorChartComponent';
import HistoricTableComponent from '../../../components/device/HistoricComponents/HistoricTableComponent/HistoricTableComponent';

const PAGE_SIZE = 10;
const INITIAL_PAGE = 0;

const HistoricDevicePage = () => {
  const { deviceUuid } = useParams() as { deviceUuid: string };

  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [data, setData] = useState<DataTelemetry[]>([]);
  const [tableData, setTableData] = useState<DataTelemetry[]>([]);
  const [page, setPage] = useState(INITIAL_PAGE);

  const [kpis, setKpis] = useState<DataTelemetryKPI>({
    totalAlerts: 0,
    mostUsedCommand: 'N/A',
    totalEvents: 0,
  });

  const getTelemetryData = useTelemetryStore((state) => state.getTelemetryDataByTimestamp);
  const getDeviceKPIs = useTelemetryStore((state) => state.getDeviceKPIs);
  const getTelemetryDataByPagination = useTelemetryStore((state) => state.getTelemetryDataByPaginationAndTimestamp);
  const totalTableData = useTelemetryStore((state) => state.totalEntitiesPagination);

  const loadRange = useCallback(
    async (startISO: string, endISO: string) => {
      setStartTime(startISO);
      setEndTime(endISO);

      const [retrieveData, retrieveKPIs] = await Promise.all([
        getTelemetryData(deviceUuid, startISO, endISO),
        getDeviceKPIs(deviceUuid, startISO, endISO),
      ]);

      setData(retrieveData);
      if (retrieveKPIs) setKpis(retrieveKPIs);
    },
    [deviceUuid, getTelemetryData, getDeviceKPIs],
  );

  const loadTable = useCallback(
    async (startISO: string, endISO: string, pageIdx = INITIAL_PAGE) => {
      setStartTime(startISO);
      setEndTime(endISO);

      const tableRetrieveData = await getTelemetryDataByPagination(deviceUuid, pageIdx, PAGE_SIZE, startISO, endISO);
      setTableData(tableRetrieveData);
    },
    [deviceUuid, getTelemetryDataByPagination],
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      const startISO = oneHourAgo.toISOString();
      const endISO = now.toISOString();

      await loadRange(startISO, endISO);
      await loadTable(startISO, endISO, INITIAL_PAGE);
    };

    fetchInitialData();
  }, [loadRange, loadTable]);

  const onChangeDates = async ([startISO, endISO]: [string, string]) => {
    await loadRange(startISO, endISO);
    await loadTable(startISO, endISO, INITIAL_PAGE);
    setPage(INITIAL_PAGE);
  };

  return (
    <Flex vertical className="HistoricDevicePage">
      <DateFilterComponent startTime={startTime} endTime={endTime} onChange={onChangeDates} />

      <div className="HistoricDevicePage__highlightsDiv">
        <HighlightComponent title="Comando más habitual" value={kpis.mostUsedCommand} />
        <HighlightComponent title="Total Alertas" value={kpis.totalAlerts.toString()} />
        <HighlightComponent title="Total eventos" value={kpis.totalEvents.toString()} />
      </div>

      <div className="HistoricDevicePage__charts">
        <SpeedChartComponent data={data} />
        <SensorChartComponent data={data} />
      </div>

      <div className="HistoricDevicePage__table">
        <HistoricTableComponent
          data={tableData}
          pagination={{
            pageSize: PAGE_SIZE,
            total: totalTableData,
            current: page + 1,
            showSizeChanger: false,
          }}
          onChange={async (pagination) => {
            const newPage = (pagination.current || 1) - 1;
            const response = await getTelemetryDataByPagination(deviceUuid, newPage, PAGE_SIZE, startTime, endTime);
            setTableData(response);
            setPage(newPage);
          }}
        />
      </div>
    </Flex>
  );
};

export default HistoricDevicePage;
